import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { Task } from 'shared/types';
import { ImageService } from '../services/imageService';
import { i18n } from '../i18n/i18n';
import { useLanguage } from '../context/LanguageContext';
import { getToolDisplayName } from '../utils/toolLabels';
import { useSheet } from '../hooks/useSheet';

interface TaskDetailsProps {
  task: Task;
  onClose: () => void;
  onUpdate: (updatedTask: Task) => void;
  /** Read-only preview (e.g. standard task before “Use”) — no checklist completion or submit */
  previewMode?: boolean;
  /** When previewing a template: label for suggested room (family room is chosen on use) */
  previewRoomLabel?: string | null;
}

const TaskDetails: React.FC<TaskDetailsProps> = ({
  task,
  onClose,
  onUpdate,
  previewMode = false,
  previewRoomLabel = null,
}) => {
  const { current_user, current_family } = useAppContext();
  const { current_language } = useLanguage();
  const [checklistState, setChecklistState] = useState<Record<string, boolean>>({});
  const [checklistImageURLs, setChecklistImageURLs] = useState<Record<string, string[]>>({});
  useSheet(true, onClose); // only rendered while open: Escape closes, page behind stays still

  // Load checklist images - supports full URLs (R2) or IndexedDB IDs
  useEffect(() => {
    const loadChecklistImages = async () => {
      if (!task.checklist) return;

      const urls: Record<string, string[]> = {};
      for (const item of task.checklist) {
        if (item.images && item.images.length > 0) {
          const itemUrls: string[] = [];
          for (const imageId of item.images) {
            if (/^https?:\/\//i.test(imageId)) {
              itemUrls.push(imageId);
            } else {
              try {
                const url = await ImageService.getImageURL(imageId);
                if (url) itemUrls.push(url);
              } catch (error) {
                console.error('Failed to load checklist image:', error);
              }
            }
          }
          urls[item.id] = itemUrls;
        }
      }
      setChecklistImageURLs(urls);
    };

    loadChecklistImages();
  }, [task.checklist]);

  if (!current_family || (!previewMode && !current_user)) {
    return <div>{i18n.t('task_details.loading')}</div>;
  }

  const assignee = task && task.assigned_to ? current_family?.members.find((m: any) => m.id === task.assigned_to) : undefined;
  const roomName = previewMode
    ? previewRoomLabel && String(previewRoomLabel).trim() !== ''
      ? String(previewRoomLabel).trim()
      : task.room_id
        ? current_family.rooms.find((r: any) => r.id === task.room_id)?.name ||
          i18n.t('task_details.preview_room_placeholder')
        : i18n.t('task_details.preview_room_placeholder')
    : task.room_id
      ? current_family.rooms.find((r: any) => r.id === task.room_id)?.name
      : 'No Room';
  const isMine = !previewMode && current_user && task.assigned_to === current_user.id;
  const allChecked = task.checklist.every(item => checklistState[item.id]);

  const getToolLabel = (tool: any): string => getToolDisplayName(tool, current_language);

  const handleChecklistToggle = (itemId: string) => {
    if (previewMode) return;
    setChecklistState(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const canSubmit = !previewMode && isMine && !task.is_completed && allChecked;

  const handleSubmitForVerification = async () => {
    try {
      const { submitTaskForVerification } = await import('../services/apiService');
      await submitTaskForVerification(task.id, 'Task completed successfully!');
      onUpdate({ ...task, is_completed: true });
    } catch (error) {
      console.error('Failed to submit task for verification:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700 p-6 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 pr-2">
              {previewMode ? i18n.t('task_details.preview_title') : i18n.t('task_details.title')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 text-2xl font-bold leading-none w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={i18n.t('task_details.close')}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 text-gray-800 dark:text-gray-100">
          {/* Task Header Image (local id or public URL) */}
          {((task as any).header_image_url || task.header_image) && (
            <div className="w-full h-48 overflow-hidden rounded-lg">
              <img
                src={(task as any).header_image_url || task.header_image}
                alt="Task header"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Task Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{task.title}</h3>
              {task.description && (
                <p className="text-gray-600 dark:text-gray-300 mt-2">{task.description}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{i18n.t('task_details.room')}</span>
                <p className="text-gray-800 dark:text-gray-100">{roomName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{i18n.t('task_details.points')}</span>
                <p className="text-gray-800 dark:text-gray-100">⭐ {task.points}</p>
              </div>
              {!previewMode && (
                <>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{i18n.t('task_details.status')}</span>
                    <span className={`meta-value status-${task.is_completed ? 'completed' : 'active'}`}>
                      {task.is_completed ? `✅ ${i18n.t('task_details.status_completed')}` : `🔄 ${i18n.t('task_details.status_active')}`}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{i18n.t('task_details.assigned_to')}</span>
                    <p className="text-gray-800 dark:text-gray-100">
                      {task.assigned_to && (
                        <span>{current_family.members.find((m: any) => m.id === task.assigned_to)?.name}</span>
                      )}
                    </p>
                  </div>
                </>
              )}
              {previewMode && task.age_group && (
                <div className="col-span-2 sm:col-span-1">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Age group</span>
                  <p className="text-gray-800 dark:text-gray-100">{task.age_group.replace(/_/g, ' ')}</p>
                </div>
              )}
            </div>

            {/* Due Date */}
            {!previewMode && task.due_date && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{i18n.t('task_details.due_date')}</span>
                <p className="text-gray-800 dark:text-gray-100">📅 {new Date(task.due_date).toLocaleDateString()}</p>
              </div>
            )}

            {/* Tools Required */}
            {task.tools && task.tools.length > 0 && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{i18n.t('task_details.tools_required')}</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {task.tools.map((toolId, index) => {
                    const tool = current_family.tools.find((t: any) => t.id === toolId);
                    return (
                      <span key={index} className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-100 px-2 py-1 rounded-md text-sm">
                        {tool ? getToolLabel(tool) : toolId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Checklist */}
          {task.checklist && task.checklist.length > 0 && (
            <div className="space-y-3">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{i18n.t('task_details.checklist')}</h4>
                {previewMode && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{i18n.t('task_details.preview_checklist_hint')}</p>
                )}
              </div>
              <div className="space-y-2">
                {task.checklist.map((item, index) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700">
                    <input
                      type="checkbox"
                      checked={previewMode ? false : !!checklistState[item.id]}
                      onChange={() => handleChecklistToggle(item.id)}
                      disabled={previewMode || !isMine || task.is_completed}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <p className={`text-sm ${!previewMode && checklistState[item.id] ? 'line-through text-gray-500' : 'text-gray-800 dark:text-gray-100'}`}>
                        {index + 1}. {item.text}
                      </p>
                      
                      {/* Checklist Item Images */}
                      {item.images && item.images.length > 0 && checklistImageURLs[item.id] && (
                        <div className="flex gap-2 mt-2">
                          {checklistImageURLs[item.id].map((imageUrl, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={imageUrl}
                              alt={`Checklist item ${index + 1} image ${imgIndex + 1}`}
                              className="w-16 h-16 object-cover rounded-md"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completion Message */}
          {!previewMode && task.is_completed && (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
              <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">{i18n.t('task_details.task_completed')}</h4>
              <p><strong>{i18n.t('task_details.completed_by')}:</strong> {current_family.members.find((m: any) => m.id === task.completed_by)?.name || 'Unknown'}</p>
              {task.completion_message && (
                <p><strong>{i18n.t('task_details.message')}:</strong> {task.completion_message}</p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {canSubmit && (
              <button
                onClick={handleSubmitForVerification}
                className="btn btn-success flex-1"
              >
                {i18n.t('task_details.submit_for_verification')}
              </button>
            )}

            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              {i18n.t('task_details.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;
